import { useTranslation } from "react-i18next";
import { Search, Plus, Pencil, Trash2, Users } from "lucide-react";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { Card } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Progress } from "@/shared/ui/progress";
import { StatusBadge } from "@/shared/ui/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { Pagination } from "@/shared/ui/pagination";
import type {
  CoachingScenarioItem,
  CoachingTaskItem,
  TaskCreateRequest,
} from "../../api/coachingService";
import { fmtDatetime, taskStatusLabel, taskStatusType } from "./helpers";
import { TaskCreateDialog } from "./TaskCreateDialog";
import { OrgScopeFilterBar } from "@/entities/org-department/ui/OrgScopeFilterBar";
import type { OrgScopeFilter } from "@/shared/hooks/useOrgScopeFilter";

const PAGE_SIZE = 20;

type Props = {
  tasks: CoachingTaskItem[];
  scenarios: CoachingScenarioItem[];
  search: string;
  onSearchChange: (v: string) => void;
  createOpen: boolean;
  onOpenCreate: () => void;
  onCloseCreate: () => void;
  onCreateTask: (data: TaskCreateRequest) => void;
  isCreating: boolean;
  onEdit: (t: CoachingTaskItem) => void;
  onDelete: (id: number) => void;
  isDeleting: boolean;
  onShowDetail?: (taskId: number) => void;
  /** MANAGER+ 写门(对齐后端派单收紧 hasRole TENANT_MANAGER);STAFF 隐藏创建/编辑/删除,只读看任务。 */
  canManage: boolean;
  // #9 (2026-07-05) — 组织级归属筛选(共享单元,替代旧「只看我的」toggle)
  orgFilter: OrgScopeFilter;
  // F 分页补齐:服务端分页(page 1 基),search 只过滤当前页(与客服未命中看板同款语义)。
  total: number;
  page: number;
  onPageChange: (page: number) => void;
};

export function TasksTab({
  tasks,
  scenarios,
  search,
  onSearchChange,
  createOpen,
  onOpenCreate,
  onCloseCreate,
  onCreateTask,
  isCreating,
  onEdit,
  onDelete,
  isDeleting,
  onShowDetail,
  canManage,
  orgFilter,
  total,
  page,
  onPageChange,
}: Props) {
  const { t } = useTranslation("coaching");
  const confirm = useConfirm();
  const filtered = search
    ? tasks.filter((t) => {
        const q = search.toLowerCase();
        return (
          t.name.toLowerCase().includes(q) ||
          (t.scenarioName ?? "").toLowerCase().includes(q) ||
          t.status.includes(q)
        );
      })
    : tasks;

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("tasksTab.searchPlaceholder")}
              className="pl-9 w-64"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* #9 (2026-07-05) — 组织级归属筛选(共享单元,替代 MANAGER+「只看我的」toggle) */}
          <OrgScopeFilterBar filter={orgFilter} />
          {canManage && (
            <Button size="sm" onClick={onOpenCreate}>
              <Plus className="h-4 w-4 mr-1" />
              {t("tasksTab.createTask")}
            </Button>
          )}
        </div>
      </div>

      <TaskCreateDialog
        open={createOpen}
        scenarios={scenarios}
        onClose={onCloseCreate}
        onSubmit={onCreateTask}
        isPending={isCreating}
      />

      <Card className="shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("tasksTab.headerName")}</TableHead>
              <TableHead>{t("tasksTab.headerScenario")}</TableHead>
              <TableHead>{t("tasksTab.headerCreator")}</TableHead>
              <TableHead>{t("tasksTab.headerAssigned")}</TableHead>
              <TableHead>{t("tasksTab.headerProgress")}</TableHead>
              <TableHead>{t("tasksTab.headerDeadline")}</TableHead>
              <TableHead>{t("tasksTab.headerStatus")}</TableHead>
              <TableHead>{t("tasksTab.headerActions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((task, i) => (
              <TableRow
                key={task.id}
                className="fx-enter"
                style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
              >
                <TableCell className="font-medium">{task.name}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="normal-case tracking-normal">
                    {task.scenarioName}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {task.creatorUserName ?? (task.creatorUserId ? `#${task.creatorUserId}` : "—")}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Users className="h-3 w-3 text-muted-foreground" />
                    <span className="text-sm">
                      {task.assignedCount}
                      {t("tasksTab.memberSuffix")}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {(() => {
                    const total = task.assignedCount || 1;
                    const pct = Math.round((task.completedCount / total) * 100);
                    return (
                      <div className="flex items-center gap-2 w-28">
                        <Progress value={pct} className="h-1.5" />
                        <span className="text-xs text-muted-foreground w-12">
                          {task.completedCount}/{task.assignedCount}
                        </span>
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {fmtDatetime(task.deadline)}
                </TableCell>
                <TableCell>
                  <StatusBadge type={taskStatusType(task.status)}>
                    {taskStatusLabel(task.status)}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1 items-center">
                    {onShowDetail && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => onShowDetail(task.id)}
                      >
                        {t("tasksTab.detail")}
                      </Button>
                    )}
                    {canManage && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label={t("tasksTab.editAria", { name: task.name })}
                          onClick={() => onEdit(task)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          aria-label={t("tasksTab.deleteAria", { name: task.name })}
                          disabled={isDeleting}
                          onClick={async () => {
                            if (
                              await confirm({
                                title: t("tasksTab.confirmDelete", { name: task.name }),
                                confirmText: t("common.delete", { defaultValue: "删除" }),
                                variant: "destructive",
                              })
                            ) {
                              onDelete(task.id);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {total > PAGE_SIZE && (
        <Pagination
          page={page - 1}
          pageSize={PAGE_SIZE}
          total={total}
          showTotal
          onPageChange={(p) => onPageChange(p + 1)}
        />
      )}
    </div>
  );
}
